import { z } from 'zod';

import type { ToPayRow } from './-types';

import { rowKey } from './-types';

/** Max times a participant may be paid (legacy MaxPayoutCount). */
export const MAX_PAYOUT_COUNT = 5;

/** Max participants payable in a single payout batch. */
export const MAX_BATCH_SIZE = 50;

/**
 * Filter values in display order. `all` = everything (param omitted), `none` =
 * never-paid (default), `'1'..'5'` = exact prior payout counts.
 */
export const PAYOUT_COUNT_FILTER_OPTIONS = [
  'all',
  'none',
  '1',
  '2',
  '3',
  '4',
  '5',
] as const;

/**
 * UI/URL value of the payout-count filter. Kept distinct from the server wire
 * string via `toPayoutCountFilterParam`.
 */
export type PayoutCountFilterValue =
  (typeof PAYOUT_COUNT_FILTER_OPTIONS)[number];

/** Default view: not-yet-paid participants. */
export const DEFAULT_PAYOUT_FILTER: PayoutCountFilterValue = 'none';

function isPayoutFilterValue(value: string): value is PayoutCountFilterValue {
  return PAYOUT_COUNT_FILTER_OPTIONS.some((v) => v === value);
}

/** Narrows an unknown URL value to a filter value, defaulting to `none`. */
export function parsePayoutFilterValue(
  value: string | undefined,
): PayoutCountFilterValue {
  if (value && isPayoutFilterValue(value)) return value;
  return DEFAULT_PAYOUT_FILTER;
}

/**
 * Route search-param schema for `payout`. TanStack deserializes a numeric URL
 * value as a number (`?payout=3` → `3`), so stringify before matching the
 * string enum or the `1`..`5` selections get silently dropped.
 */
export const payoutSearchSchema = z
  .preprocess(
    (value) => (value === undefined ? undefined : String(value)),
    z.enum(PAYOUT_COUNT_FILTER_OPTIONS),
  )
  .optional()
  .catch(undefined);

/**
 * Maps the UI value to the server `payoutCountFilter` query string:
 * `all` → undefined (omitted); all other values pass through unchanged.
 */
export function toPayoutCountFilterParam(
  value: PayoutCountFilterValue,
): string | undefined {
  return value === 'all' ? undefined : value;
}

export function isRowPayDisabled(payoutCount: number): boolean {
  return payoutCount >= MAX_PAYOUT_COUNT;
}

/** rowKeys of every maxed row — fed to the table's `disabledKeys`. */
export function buildDisabledKeys(rows: ToPayRow[]): Set<string> {
  const keys = new Set<string>();
  for (const row of rows) {
    if (isRowPayDisabled(row.payoutCount)) keys.add(rowKey(row));
  }
  return keys;
}

/**
 * Select-all is disabled when a search is active, no rows are visible, or every
 * visible row is maxed. Deriving the last case from the disabled set (not a
 * `filterValue === '5'` proxy) also covers an all/none page of only-maxed rows.
 */
export function isSelectAllDisabled({
  hasSearch,
  visibleCount,
  allRowsDisabled,
}: {
  hasSearch: boolean;
  visibleCount: number;
  allRowsDisabled: boolean;
}): boolean {
  return hasSearch || visibleCount === 0 || allRowsDisabled;
}

/**
 * Drops `payout` from the URL for the default `none`, sets the explicit value
 * otherwise. Mirrors `nextSearchOnVisibilityFilterChange`.
 */
export function nextSearchOnPayoutFilterChange<
  T extends { payout?: PayoutCountFilterValue },
>(prev: T, next: PayoutCountFilterValue): T {
  return {
    ...prev,
    payout: next === DEFAULT_PAYOUT_FILTER ? undefined : next,
  };
}
